import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { X_CAPABILITY } from '@asp/contracts';
import { canApproveVariant, canRequestChanges, toIsoUtc } from '@asp/domain';
import type { VariantState } from '@asp/domain';
import type { VariantDetail } from '../api/client.js';
import {
  approveVariant, cancelSchedule, fetchVariant, requestChanges, scheduleVariant,
} from '../api/client.js';
import { Counter } from '../components/Counter.js';
import { SchedulePanel } from '../components/SchedulePanel.js';
import { Stamp } from '../components/Stamp.js';
import { StateChip } from '../components/StateChip.js';
import { useDictionary } from '../i18n/context.js';
import { failureReasonLabel } from '../i18n/failure.js';

// ---------------------------------------------------------------------------
// Review detail (UI structure spec section 8.2): identity, why review is
// required, platform preview, field content, validation, schedule, recent
// activity, then actions -- in that order, because the order is the
// argument (see design-system/src/screens/review.html's own note).
// ---------------------------------------------------------------------------

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: VariantDetail };

export const ReviewRoute = (): React.JSX.Element => {
  const dict = useDictionary();
  const { variantId } = useParams<{ variantId: string }>();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = (): void => {
    if (!variantId) return;
    setState({ status: 'loading' });
    fetchVariant(variantId)
      .then((data) => setState({ status: 'ready', data }))
      .catch((err: unknown) => setState({ status: 'error', message: err instanceof Error ? err.message : String(err) }));
  };

  useEffect(load, [variantId]);

  if (!variantId) return <p className="t-body">{dict.ui.review.notFound}</p>;
  if (state.status === 'loading') return <p className="t-meta">{dict.ui.review.loading}</p>;
  if (state.status === 'error') return <p className="t-meta">{dict.ui.review.loadFailed}</p>;

  const { variant, project, campaign, connection, revision, validation, schedule, publication, attempts, activity } = state.data;
  const validationPassed = validation.issues.length === 0;
  const variantState = variant.state as VariantState;

  // Whole-branch review C2: `canApprove` used to be computed from
  // validation issues alone, never consulting the variant's own state --
  // so a `published` variant (whose already-posted content still passes
  // validation) still rendered an enabled Approve button. `canApproveVariant`
  // (@asp/domain) is the exact precondition `approveVariant`
  // (packages/application/src/review/approve-variant.ts) enforces: true
  // only from `needs_review`.
  const canApprove = canApproveVariant(variantState) && validationPassed;
  const requestChangesEligible = canRequestChanges(variantState);
  // The attempt that decided the outcome -- the newest failure, not the
  // first, since a retry that failed differently is the current reason.
  const lastFailure = [...attempts].reverse().find((a) => !a.ok) ?? null;
  const isPublished = variantState === 'published';

  // Phase 1 has exactly one automation mode (`manual`, design doc section
  // 5.4) and approving a revision never itself creates or moves a
  // schedule -- scheduling is always a distinct, later, explicit operator
  // action (schedule-variant.ts never runs as a side effect of approval).
  // So "approving publishes this post" can only ever be true here in the
  // one case where an active schedule already exists for a variant that is
  // still eligible for approval -- UI spec 8.3: "If approval would cause an
  // immediate or near-immediate publication ... the action summary states
  // that result before confirmation." The old code rendered this banner
  // unconditionally, including on a `published` variant where it was never
  // true.
  const approvalCausesImmediatePublication = canApprove && schedule !== null;

  const runAction = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack" style={{ gap: 'var(--sp-5)' }}>
      <div className="stack-2">
        <Link className="btn btn--ghost" to="/home">{dict.ui.review.backToHome}</Link>
        <div className="between">
          <div className="stack-1">
            <h1 className="t-title">{dict.ui.review.title}</h1>
            <p className="t-meta">
              {project.name} <span className="mono-meta">{project.website}</span> — {campaign.name}
              {connection && (
                <span className="mono-meta">
                  {' — '}{connection.platform === 'pinterest' ? 'Pinterest' : 'X'} @{connection.accountHandle}
                </span>
              )}
            </p>
          </div>
          <div className="row">
            <StateChip state={variant.state} />
            <span className="mono-meta">{dict.ui.review.revisionNumber(revision.number)}</span>
            {revision.origin === 'mcp' && <span className="origin origin--ai">{revision.originClient ?? dict.ui.review.mcpClientFallback}</span>}
          </div>
        </div>
      </div>

      {revision.origin === 'mcp' && (
        <div className="panel">
          <div className="panel__head"><h2 className="t-section">{dict.ui.review.whyReview}</h2></div>
          <div className="panel__body">
            <div className="alert alert--ai">
              <div className="stack-1">
                <div className="alert__body">{dict.ui.review.whyReviewAiBody}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="panel__head">
          <h2 className="t-section">{dict.ui.review.platformPreview(revision.content.kind === 'pinterest' ? 'Pinterest' : 'X')}</h2>
        </div>
        <div className="panel__body">
          {revision.content.kind === 'x'
            ? <p className="t-body ja">{revision.content.text}</p>
            : (
              <div className="stack-1">
                <p className="t-section">{revision.content.title}</p>
                <p className="t-body ja">{revision.content.description}</p>
                {revision.content.link && <p className="mono-meta">{revision.content.link}</p>}
              </div>
            )}
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <h2 className="t-section">{dict.ui.review.fieldContent}</h2>
          <span className="mono-meta">{dict.ui.review.revisionImmutable(revision.number)}</span>
        </div>
        <div className="panel__body stack">
          {revision.content.kind === 'x'
            ? (
              <>
                <div className="field">
                  <span className="field__label">{dict.ui.review.postText}</span>
                  <p
                    className="t-body ja"
                    style={{ padding: 'var(--sp-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-control)', background: 'var(--surface-sunken)' }}
                  >
                    {revision.content.text}
                  </p>
                  <div className="field__foot">
                    <span className="field__hint">{dict.ui.review.readOnlyHint}</span>
                    <Counter text={revision.content.text} profile={X_CAPABILITY} />
                  </div>
                </div>

                {revision.content.media.length > 0 && (
                  <>
                    <hr className="rule" />
                    <div className="stack-2">
                      <span className="field__label">{dict.ui.review.attachedMedia(revision.content.media.length)}</span>
                      {revision.content.media.map((m) => (
                        <p key={m.assetId} className="t-meta">{m.altText}</p>
                      ))}
                    </div>
                  </>
                )}
              </>
            )
            : (
              <>
                <div className="field">
                  <span className="field__label">{dict.ui.review.pinTitle}</span>
                  <p className="t-body ja">{revision.content.title}</p>
                </div>
                <div className="field">
                  <span className="field__label">{dict.ui.review.pinDescription}</span>
                  <p className="t-body ja">{revision.content.description}</p>
                </div>
                <div className="field">
                  <span className="field__label">{dict.ui.review.pinBoard}</span>
                  <p className="mono-meta">{revision.content.boardId}</p>
                </div>
                <div className="field">
                  <span className="field__label">{dict.ui.review.pinLink}</span>
                  <p className="mono-meta">{revision.content.link ?? dict.ui.review.pinLinkNone}</p>
                </div>
                <hr className="rule" />
                <div className="stack-2">
                  <span className="field__label">{dict.ui.review.pinImageAlt}</span>
                  <p className="t-meta">{revision.content.image.altText}</p>
                </div>
              </>
            )}
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <h2 className="t-section">{dict.ui.review.validation}</h2>
          <span className={validationPassed ? 'chip chip--done' : 'chip chip--danger'}>
            {validationPassed ? dict.ui.review.validationPassed : dict.ui.review.validationFailed}
          </span>
        </div>
        <div className="panel__body stack-2">
          {validationPassed
            ? <p className="t-meta">{dict.ui.review.validationPassed}</p>
            : (
              <ul>
                {validation.issues.map((issue) => (
                  <li key={`${issue.field}-${issue.code}-${issue.index ?? 0}`} className="t-body">{issue.message}</li>
                ))}
              </ul>
            )}
        </div>
      </div>

      <div className="panel">
        <div className="panel__head"><h2 className="t-section">{dict.ui.review.schedule}</h2></div>
        <div className="panel__body">
          {schedule
            ? (
              <div className="row wrap" style={{ gap: 'var(--sp-6)' }}>
                <Stamp instant={schedule.publishAtUtc} />
              </div>
            )
            : <p className="t-meta">{variant.approvedRevisionId ? dict.ui.review.notScheduled : dict.ui.review.approveFirst}</p>}
        </div>
      </div>

      {/* Failure-state affordance. Until this existed the screen could show
          that a variant was `failed` and nothing about why: the reason is in
          `publication_attempts`, which the route did not return. A real 402
          from X (no credit) therefore reached the operator as a variant that
          had simply stopped. The panel names the cause, states plainly that
          nothing was posted -- the first question anyone asks -- and shows
          every attempt, because a post that failed twice and then succeeded
          is a different story from one that failed once. */}
      {variantState === 'failed' && (
        <div className="panel">
          <div className="panel__head"><h2 className="t-section">{dict.ui.review.publishFailed}</h2></div>
          <div className="panel__body stack-3">
            <div className="row wrap">
              <span className="chip chip--danger">{dict.states.failed}</span>
              <span className="t-label">{failureReasonLabel(dict, lastFailure?.category ?? null)}</span>
            </div>
            <p className="t-body">{dict.ui.review.publishFailedHint}</p>
            {attempts.length > 0 && (
              <table className="table">
                <thead>
                  <tr>
                    <th className="t-label">{dict.ui.review.attemptNumber}</th>
                    <th className="t-label">{dict.ui.review.attemptResult}</th>
                    <th className="t-label">{dict.ui.review.attemptReason}</th>
                    <th className="t-label">{dict.ui.review.attemptDetail}</th>
                  </tr>
                </thead>
                <tbody>
                  {attempts.map((a) => (
                    <tr key={`${a.attempt}-${a.at}`}>
                      <td className="mono-meta">{a.attempt}</td>
                      <td className="t-body">{a.ok ? dict.ui.review.attemptOk : dict.ui.review.attemptFailed}</td>
                      <td className="t-body">{a.ok ? '—' : failureReasonLabel(dict, a.category)}</td>
                      <td className="mono-meta">{a.message ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Published-state affordance -- UI structure spec 18.2: "Published |
          View published record | Open remote post" -- and whole-branch
          review C2's explicit ask: a plain statement that a published post
          cannot be recalled by this application. */}
      {isPublished && publication && (
        <div className="panel">
          <div className="panel__head"><h2 className="t-section">{dict.ui.review.publishedRecord}</h2></div>
          <div className="panel__body stack-2">
            <a className="btn btn--primary" href={publication.publicUrl} target="_blank" rel="noreferrer">
              {dict.ui.review.openPublishedPost}
            </a>
            <p className="t-meta">{dict.ui.review.publishedCannotBeRecalled}</p>
          </div>
        </div>
      )}

      <SchedulePanel
        state={variantState}
        approvedRevisionId={variant.approvedRevisionId}
        currentSchedulePublishAtUtc={schedule?.publishAtUtc ?? null}
        saving={busy}
        onSave={async (publishAtRfc3339) => {
          await runAction(() => scheduleVariant(variant.id, publishAtRfc3339));
        }}
      />

      <div className="panel">
        <div className="panel__head"><h2 className="t-section">{dict.ui.review.recentActivity}</h2></div>
        <div className="panel__body panel__body--flush">
          {activity.length === 0
            ? <div className="empty"><div className="empty__title">{dict.ui.review.noActivity}</div></div>
            : (
              <table className="table">
                <thead>
                  <tr>
                    <th>{dict.ui.review.activityWhen}</th>
                    <th>{dict.ui.review.activityOrigin}</th>
                    <th>{dict.ui.review.activityAction}</th>
                    <th>{dict.ui.review.activityResult}</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.map((e) => (
                    <tr key={e.id}>
                      <td><Stamp instant={e.at} compact /></td>
                      <td><span className={e.origin === 'mcp' ? 'origin origin--ai' : 'origin origin--system'}>{e.origin}</span></td>
                      <td>{e.action}</td>
                      <td><span className={e.result === 'ok' ? 'chip chip--done' : 'chip chip--danger'}>{e.result}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      </div>

      <div className="panel">
        <div className="panel__head"><h2 className="t-section">{dict.ui.review.actions}</h2></div>
        <div className="panel__body stack">
          {actionError && (
            <div className="alert alert--danger">
              <div className="alert__body">{actionError}</div>
            </div>
          )}

          {approvalCausesImmediatePublication && (
            <div className="alert alert--attention">
              <div className="stack-1">
                <div className="alert__body">
                  {dict.ui.review.approveConsequence(
                    schedule ? new Date(schedule.publishAtUtc).toLocaleString() : '—',
                    schedule ? toIsoUtc(new Date(schedule.publishAtUtc)) : '—',
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="stack-1">
            <div className="row wrap" style={{ gap: 'var(--sp-3)' }}>
              <button
                type="button"
                className="btn btn--primary btn--lg"
                disabled={!canApprove || busy}
                onClick={() => { void runAction(() => approveVariant(variant.id, variant.currentRevisionId)); }}
              >
                {dict.actions.approve}
              </button>
            </div>
            {!canApproveVariant(variantState) && (
              <span className="t-meta">{dict.ui.review.approveUnavailable}</span>
            )}
          </div>

          <hr className="rule" />

          {requestChangesEligible
            ? (
              <div className="stack-2" style={{ maxWidth: 560 }}>
                <div className="field">
                  <label className="field__label" htmlFor="rc-reason">{dict.ui.review.reasonLabel}</label>
                  <textarea
                    id="rc-reason"
                    className="textarea"
                    placeholder={dict.ui.review.reasonPlaceholder}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <div className="field__foot">
                    <span className="field__hint">{dict.ui.review.reasonHint}</span>
                  </div>
                </div>
                {reason.trim() === ''
                  ? (
                    <div className="blocked">
                      <button type="button" className="btn" disabled>{dict.actions.requestChanges}</button>
                      <span className="blocked__why">{dict.ui.review.requestChangesBlocked}</span>
                    </div>
                  )
                  : (
                    <button
                      type="button"
                      className="btn"
                      disabled={busy}
                      onClick={() => { void runAction(() => requestChanges(variant.id, reason)); }}
                    >
                      {dict.actions.requestChanges}
                    </button>
                  )}
              </div>
            )
            : (
              <div className="blocked">
                <button type="button" className="btn" disabled>{dict.actions.requestChanges}</button>
                <span className="blocked__why">{dict.ui.review.requestChangesUnavailable}</span>
              </div>
            )}

          <hr className="rule" />

          <div className="stack-2">
            <div className="blocked">
              <button type="button" className="btn" disabled>{dict.ui.review.edit}</button>
              <span className="blocked__why">{dict.ui.review.editBlocked}</span>
            </div>
            {schedule && (
              <div className="row-t" style={{ gap: 'var(--sp-4)' }}>
                <button
                  type="button"
                  className="btn btn--danger"
                  style={{ flex: '0 0 auto' }}
                  disabled={busy}
                  onClick={() => { void runAction(() => cancelSchedule(variant.id)); }}
                >
                  {dict.actions.cancelSchedule}
                </button>
                <span className="t-body muted" style={{ maxWidth: '72ch' }}>{dict.ui.review.cancelScheduleHint}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
